// --- React & Redux
import React, { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
// --- External UI Components
import { Drawer } from 'antd';
import Button from '@dtci/spellbook/Button';
import Input from '@dtci/spellbook/Input';
import Select from '@dtci/spellbook/Select';
import Typography from '@dtci/spellbook/Typography';
// --- Components
import { CustomFieldTree } from './tree';
// Styles
import './styles.scss';
// --- Hooks & Actions
import { useTemplatesActions } from 'datastage/store/templates/useTemplates';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToasts } from 'datastage/utils/hooks/useToasts';
import { datastageApi } from 'datastage/api';

/**
 * Custom Fields Drawer
 *
 * Features:
 * - Displays a wide drawer for managing custom fields in upload templates
 * - Saved Lists dropdown in header, save controls in footer
 *
 * @returns {Element}
 * @constructor
 */
export const CustomFieldsDrawer = () => {
    const { uploadForm, isDrawerOpen, customFieldList, selectedCustomList } = useSelector(
        (state) => state.templates,
    );
    const treeRef = useRef(null);
    const { updateTemplates } = useTemplatesActions();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showSuccessToast, showErrorToast } = useToasts();
    const [customListName, setCustomListName] = useState('');

    // Reset name when drawer opens
    useEffect(() => {
        if (isDrawerOpen) {
            setCustomListName(selectedCustomList?.label || '');
        }
    }, [isDrawerOpen]);

    // Update name when selected list changes
    useEffect(() => {
        if (selectedCustomList) {
            setCustomListName(selectedCustomList.label || '');
        }
    }, [selectedCustomList]);

    const onCloseDrawer = () => {
        updateTemplates({
            isDrawerOpen: false,
        });
    };

    const onApply = () => {
        // Commit local changes to Redux
        if (treeRef.current) {
            treeRef.current.applyChanges();
        }
        onCloseDrawer();
    };

    // Handle custom list selection change
    const handleCustomListChange = (value) => {
        updateTemplates({ selectedCustomList: value });
        setCustomListName(value ? value.label : '');
    };

    // Handle custom list name input change
    const handleCustomListNameChange = (event) => {
        const regex = /^[a-zA-Z0-9\s]*$/;
        const inputValue = event?.target?.value || '';
        if (regex.test(inputValue)) {
            setCustomListName(inputValue);
        }
    };

    // Handle deleting a custom list
    const handleDeleteCustomList = async (customListId) => {
        setLoadingTrue();
        try {
            const res = await datastageApi.deleteRLCustomList(customListId);
            if (res?.code) {
                showErrorToast(res?.message || 'Failed to delete custom list.');
                return;
            }
            const newCustomFieldList = await datastageApi.rlCustomListSearch(uploadForm);
            updateTemplates({
                selectedCustomList: null,
                customFieldList: newCustomFieldList,
            });
            setCustomListName('');
            showSuccessToast('Custom list deleted successfully.');
        } catch (error) {
            showErrorToast(error?.message || 'Failed to delete custom list.');
        } finally {
            setLoadingFalse();
        }
    };

    // Handle saving (update existing or create new)
    const handleSave = async () => {
        if (!treeRef.current) return;
        const selectedFieldKeys = treeRef.current.getSelectedFieldKeys();
        
        setLoadingTrue();
        try {
            if (selectedCustomList) {
                // Update existing
                const res = await datastageApi.saveRLCustomList({
                    id: selectedCustomList.value,
                    fieldKeys: selectedFieldKeys,
                    templateName: selectedCustomList.templateName || customListName.trim(),
                    name: customListName.trim(),
                });
                if (res?.code) {
                    showErrorToast(res?.message || 'Failed to save custom list.');
                    return;
                }
                showSuccessToast('Custom list saved successfully.');
            } else {
                // Create new
                const res = await datastageApi.createRLCustomList({
                    actionKey: uploadForm?.action?.value,
                    dateFormatKey: uploadForm?.dateFormat?.value,
                    fieldKeys: selectedFieldKeys,
                    moduleKey: uploadForm?.entityType?.value,
                    name: customListName.trim(),
                    templateKey: uploadForm?.template?.value,
                    templateName: uploadForm?.template?.label,
                });
                if (res?.code) {
                    showErrorToast(res?.message || 'Failed to create custom list.');
                    return;
                }
                showSuccessToast('Custom list created successfully.');
            }
            // Refresh list
            const newCustomFieldList = await datastageApi.rlCustomListSearch(uploadForm);
            const newSelected = newCustomFieldList.find(
                (item) => item.label === customListName.trim()
            );
            updateTemplates({
                selectedCustomList: newSelected || null,
                customFieldList: newCustomFieldList,
            });
        } catch (error) {
            showErrorToast(error?.message || 'Failed to save custom list.');
        } finally {
            setLoadingFalse();
        }
    };

    const isSaveEnabled = customListName.trim().length > 0;

    // Build drawer title from form selections
    const drawerTitle = [
        'Custom Field List',
        uploadForm.action?.label,
        uploadForm.entityType?.label,
        uploadForm.template?.label,
    ].filter(Boolean).join(' - ');

    return (
        <Drawer
            className="custom-fields-drawer"
            title={drawerTitle}
            width={750}
            placement="right"
            open={isDrawerOpen}
            onClose={onCloseDrawer}
            footer={
                <div className="custom-fields-drawer-footer">
                    <Typography variant={'T15'} italic>
                        <span className="required-asterisk">*</span>Required Fields cannot be deselected
                    </Typography>
                    <Button
                        sizeType="small"
                        styleType="link"
                        iconType="fa-solid fa-check"
                        onClick={onApply}
                    >
                        Apply
                    </Button>
                </div>
            }
        >
            {/* Saved Lists Card Section */}
            <div className="saved-lists-card">
                <div className="saved-lists-card-label">Saved Lists</div>
                <div className="saved-lists-card-content">
                    <Select
                        options={customFieldList}
                        isClearable
                        isSearchable
                        onChange={handleCustomListChange}
                        value={selectedCustomList}
                        placeholder="Select a saved list..."
                        className="saved-lists-select"
                        formatOptionLabel={(option, { context }) => (
                            <div className="custom-field-option-label">
                                <span>{option.label}</span>
                                {context === 'menu' && (
                                    <Button
                                        iconType="fa-solid fa-trash"
                                        sizeType="tiny"
                                        styleType="link"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteCustomList(option.value);
                                        }}
                                    />
                                )}
                            </div>
                        )}
                    />
                    <div className="saved-lists-save-row">
                        <Input
                            value={customListName}
                            onChange={handleCustomListNameChange}
                            maxLength={30}
                            width="large"
                            id="newList"
                            name="newList"
                            placeholder={selectedCustomList ? 'Update name...' : 'New list name...'}
                        />
                        <Button
                            iconType={selectedCustomList ? 'fa-solid fa-save' : 'fa-solid fa-plus'}
                            styleType="link"
                            sizeType="small"
                            onClick={handleSave}
                            disabled={!isSaveEnabled}
                        />
                    </div>
                </div>
            </div>

            <CustomFieldTree ref={treeRef} />
        </Drawer>
    );
};
